import { isNil } from 'lodash';
import { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import {
    IFlowTheme,
    IImage,
    ServiceItemType,
    ServiceItemUnit,
    TServiceSelectionItemsStylesKeys
} from '~/types/model.types';
import {
    IESPackageResponse,
    IPackageResponse,
    IServiceResponse,
    TESServiceOrPackage
} from '~/types/server.types';
import { CXServiceItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-item-block.model';
import { CXServiceSelectionItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-selection-item-block.model';
import { ServiceItemBlockTypes, TBlock } from '~/types/builder.types';
import { TBlockFakeCkEditorStylesKeys } from '~/types/block-styles.types';
import { IOption } from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.types';

export const getServiceItemUnits = (
    isPlural: boolean,
    customServiceUnits?: string[]
): IOption[] => {
    const units = Object.keys(ServiceItemUnit).map(unit => ({
        label: isPlural && unit !== 'None' ? `${unit}s` : unit,
        value: ServiceItemUnit[unit],
        isCustomUnit: false
    }));

    customServiceUnits?.forEach(unit =>
        units.push({
            label: unit,
            value: unit,
            isCustomUnit: true
        })
    );

    return units;
};

export function getImageForPackageOrService(
    item: TESServiceOrPackage | IServiceResponse
) {
    if ('package_service_image' in item) {
        return item.package_service_image;
    }

    if ('package_image' in item) {
        return item.package_image;
    }

    return null;
}

// convert an IPackageResponse to an elastic search service. Just to keep a consistent api in certain functions
export function packageToEsService(
    pkg: IPackageResponse,
    item: IESPackageResponse,
    isWithoutTaxes?: boolean
): {
    service_item: TESServiceOrPackage;
    service_image: IImage;
    service_asset: string;
} {
    return {
        service_item: {
            package_id: pkg._id,
            name: pkg.title,
            quantity: pkg.quantity,
            unit: pkg.unit,
            price_per_unit: pkg.price_per_unit || pkg.base_price,
            description: pkg.description,
            items_count: item?.items_count,
            updated_at: item.updated_at,
            sent_on: item.sent_on,
            source: item.source, // file / flow
            source_id: item.source_id,
            source_title: item.source_title,
            flow_content_id: item.flow_content_id,
            package_image: item.package_image,
            block_id: item.block_id,
            qb_item_id: item.qb_item_id,
            type: ServiceItemType.Package,
            ...(isWithoutTaxes
                ? {}
                : {
                      // Todo: add support for packages from old builder, should remove when this ticket is deployed - SMART-6004
                      tax_1: pkg.taxable || pkg.tax_1,
                      tax_2: pkg.svcable || pkg.tax_2,
                      tax_3: pkg.tax_3
                  })
        },
        service_image: item.package_image,
        service_asset: item.service_asset
    };
}

export function esServiceToServiceItem(
    esService: TESServiceOrPackage | IServiceResponse
) {
    let serviceItem;
    if ('package_id' in esService) {
        serviceItem = {
            title: esService.name,
            price_per_unit: esService.price_per_unit,
            description: esService.description,
            quantity: esService.quantity,
            unit: esService.unit,
            tax_1: esService.tax_1,
            tax_2: esService.tax_2,
            tax_3: esService.tax_3,
            qb_item_id: esService.qb_item_id
        };
    } else {
        serviceItem = {
            quantity: esService.quantity,
            description: esService.description,
            title: esService.name,
            tax_1: esService.tax_1,
            tax_2: esService.tax_2,
            tax_3: esService.tax_3,
            price_per_unit: esService.price_per_unit,
            price: esService.price,
            unit: esService.unit,
            qb_item_id: esService.qb_item_id
        };
    }

    return {
        service_item: serviceItem,
        service_image: getImageForPackageOrService(esService),
        service_asset: esService.service_asset
    };
}

export const INITIAL_SERVICE_ITEM_VALUES = {
    title: '',
    description: '',
    price: 0,
    price_per_unit: null,
    quantity: null,
    unit: null,
    tax_1: false,
    tax_2: false,
    tax_3: false
};

export function isLastServiceSelectionItemInBlock(block: ServiceItemBlock) {
    return (
        block?.isServiceSelectionItemBlock &&
        (block as ServiceSelectionItemBlock).serviceSelectionBlock.serviceItems
            .length == 1
    );
}

export function isLastSubItemOnPackage(block: TBlock) {
    const serviceItemBlockTypes = Object.values(ServiceItemBlockTypes);
    return (
        serviceItemBlockTypes.includes(block.type as ServiceItemBlockTypes) &&
        serviceItemBlockTypes.includes(
            block.parent.type as ServiceItemBlockTypes
        ) &&
        block.parent.childBlocks.length === 1
    );
}

interface Props {
    block: ServiceItemBlock | CXServiceItemBlock;
    styleKey: TBlockFakeCkEditorStylesKeys;
    parentKey?: TServiceSelectionItemsStylesKeys;
    mainKey: TServiceSelectionItemsStylesKeys;
    theme: IFlowTheme;
    useDefaultTheme?: boolean;
    themeKey?: string;
    ignoreParentStyles?: boolean;
}
export const getItemStyleAttr = ({
    block,
    styleKey,
    parentKey,
    mainKey,
    theme,
    useDefaultTheme = false,
    themeKey = null,
    ignoreParentStyles = false
}: Props) => {
    let itemStyleAttr = (
        block as ServiceSelectionItemBlock | CXServiceSelectionItemBlock
    )?.serviceSelectionBlock?.styles?.[mainKey]?.[styleKey];

    if (parentKey && !ignoreParentStyles) {
        itemStyleAttr = isNil(itemStyleAttr)
            ? (block as ServiceSelectionItemBlock | CXServiceSelectionItemBlock)
                  ?.serviceSelectionBlock?.styles?.[parentKey]?.[styleKey]
            : itemStyleAttr;
    }

    if (useDefaultTheme) {
        itemStyleAttr = isNil(itemStyleAttr)
            ? theme.styles?.[themeKey || styleKey]
            : itemStyleAttr;
    }

    return itemStyleAttr;
};
